import isArray from 'lodash/isArray'
import { call, put, select } from 'redux-saga/effects'

import { ACTIVE_OPACITY_VALUE, INACTIVE_OPACITY_VALUE } from '../../../shared/constants/colors'
import { NEW_IMUP_JOIST_TYPES_GROUP } from '../../../shared/constants/drawable-types'
import managers from '../../lib/managers'
import PaperManager from '../../lib/managers/PaperManager'
import { Select, Workspace } from '../../lib/toolBoxes/2D'
import {
    ActiveGeometryGroup,
    GeometricDrawable,
    GeometryVisibilityRecord,
    selectActiveGeometryGroup,
    toggleGeometriesVisibility,
    updateGeometryVisibility,
} from '../../slices/geometry'
import { selectIsConflictResolutionInProgress } from '../../slices/masterSetPlan'

const prepareDrawableId = (drawableId: number | number[]) => {
    return isArray(drawableId) ? drawableId : [drawableId]
}

export function* handleToggleActiveDrawable2D(drawableId: number | number[] | null) {
    const manager: PaperManager | null = yield call(managers.get2DManager)

    if (!manager) return

    const [workspaceTool, selectTool]: [Workspace, Select] = yield call(manager.getTools, [Workspace.NAME, Select.NAME])

    yield call(selectTool.removeHandles)

    const activeDrawableGroup: ActiveGeometryGroup | null = yield select(selectActiveGeometryGroup)

    if (!activeDrawableGroup) return

    const preparedDrawableIds = prepareDrawableId(drawableId || activeDrawableGroup.openings.map((o) => o.id))
    const isConflictResolutionInProgress: boolean = yield select(selectIsConflictResolutionInProgress)

    if (isConflictResolutionInProgress) {
        const openingsById = new Map(
            activeDrawableGroup.openings
                .filter((opening) => preparedDrawableIds.includes(opening.id))
                .map((opening) => [opening.id, opening] as const)
        )
        const openingsByLocationId = new Map(
            Array.from(openingsById.values()).flatMap((opening) =>
                opening.opening_locations.map((location) => [location.id, opening] as const)
            )
        )
        const modifications: paper.Item[] = yield call(
            workspaceTool.getItemsWithCriteria,
            'data',
            (data) =>
                openingsByLocationId.has(data.materialModificationConflict?.material_location_id) ||
                (data.createdDuringConflictResolution && openingsById.has(data.drawable_id))
        )
        const visibilityUpdate: GeometryVisibilityRecord = {}

        for (const item of modifications) {
            const opening = item.data.materialModificationConflict
                ? openingsByLocationId.get(item.data.materialModificationConflict.material_location_id)!
                : openingsById.get(item.data.drawable_id)!
            const isActive = Boolean(opening.isActive)

            item.data.isActive = isActive

            // Group selection locks items by drawable_id, which conflict paths do not have.
            if (isActive) item.locked = false

            if (!item.visible && isActive) {
                item.visible = true
                visibilityUpdate[opening.id] = true
            }

            const opacityValue = isActive ? ACTIVE_OPACITY_VALUE : INACTIVE_OPACITY_VALUE

            if (item.fillColor) item.fillColor.alpha = opacityValue
            if (item.strokeColor) item.strokeColor.alpha = opacityValue
        }

        if (Object.keys(visibilityUpdate).length) {
            yield put(updateGeometryVisibility(visibilityUpdate))
        }

        return
    }

    const everySelected = activeDrawableGroup.openings.every((opening) => opening.isActive)
    const everyDeselected = activeDrawableGroup.openings.every((opening) => !opening.isActive)

    const items: paper.Item[] = yield call(
        workspaceTool.getItemsWithCriteria,
        'data',
        (data: { drawable_id: number }) => preparedDrawableIds.includes(data.drawable_id)
    )

    for (const item of items) {
        const {
            visible,
            data: { isActive = false, drawing_type },
            children,
        } = item

        const newIsActive = everySelected || (!everyDeselected && !isActive)

        // update immediately so all following logic uses correct state
        item.data.isActive = newIsActive

        if (!visible && newIsActive && !children) {
            yield put(toggleGeometriesVisibility([item.data.drawable_id]))
        }

        if (visible && !children) {
            const opacityValue = newIsActive ? ACTIVE_OPACITY_VALUE : INACTIVE_OPACITY_VALUE

            if (item.fillColor) {
                item.fillColor.alpha = opacityValue
            }
            if (item.strokeColor) {
                item.strokeColor.alpha = opacityValue
            }
        }

        if (
            NEW_IMUP_JOIST_TYPES_GROUP.includes(drawing_type) &&
            children &&
            item?.data?.additionalData?.raw_joist_length
        ) {
            item.visible = newIsActive
        }
    }
}

export function* formatItemWithSelectedStatus(
    workspaceTool: Workspace,
    selectTool: Select,
    drawable: GeometricDrawable,
    drawableItem?: paper.Path | null
) {
    const item: paper.Path | null =
        drawableItem !== undefined ? drawableItem : yield call(workspaceTool.getItemWithDrawableId, drawable.id)

    if (!item) return

    if (!drawable.isActive) yield call(selectTool.removeHandles)

    const colorAlpha = drawable.isActive ? item.data.activeOpacityValue : item.data.inactiveOpacityValue

    if (item.fillColor) item.fillColor.alpha = colorAlpha
    if (item.strokeColor) item.strokeColor.alpha = colorAlpha
}
